import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, ArrowLeft, CreditCard, FilePlus2, History, Loader2, Plus, ReceiptText, RotateCcw, Search, Trash2 } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import type { SupportedLanguage } from '../i18n/translations';
import { useApiClient } from '../lib/apiClient';
import { formatCurrency } from '../i18n/locale';
import { useOrgFormatting } from '../hooks/useOrgFormatting';

type BillingStatus = 'UNPAID' | 'PENDING' | 'PAID' | 'REFUNDED';

type BillingService = {
  id: string;
  code: string;
  appointmentTypeId?: string | null;
  appointmentTypeName?: string | null;
  name: string;
  description?: string;
  price: number;
  category?: string;
  serviceCode?: string;
  iscCode?: string;
  ccsdCode?: string;
};

type BillingProduct = {
  id: string;
  code: string;
  name: string;
  description?: string;
  price: number;
  inventory: number;
};

type CatalogResponse = {
  services: BillingService[];
  products: BillingProduct[];
};

type ProviderRow = { id: string; firstName: string; lastName: string; title?: string };

type AppointmentRow = {
  id: string;
  startTime: string;
  endTime: string;
  appointmentType?: { name: string };
};

type InvoiceItem = {
  id: string;
  serviceId?: string;
  productId?: string;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
};

type PaymentRow = {
  id: string;
  amount: number;
  method: string;
  reference?: string;
  createdAt: string;
};

type InvoiceRow = {
  id: string;
  invoiceNumber?: string;
  date: string;
  dueDate?: string | null;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  balance: number;
  status: BillingStatus;
  notes?: string;
  items: InvoiceItem[];
  payments: PaymentRow[];
  appointment?: {
    id: string;
    startTime: string;
    endTime: string;
    appointmentTypeName?: string | null;
    performedServiceName?: string | null;
    serviceName?: string | null;
  };
  provider?: { firstName: string; lastName: string };
};

type SummaryResponse = {
  invoiceCount: number;
  totalBilled: number;
  outstandingBalance: number;
  unpaidCount: number;
  pendingCount: number;
  paidCount: number;
};

type InvoiceAuditRow = { id: string; action: string; entity: string; createdAt: string; userName?: string | null; metadata?: Record<string, unknown> };
type FinancialStatement = {
  generatedAt: string;
  totals: { billed: number; paid: number; balance: number; accountBalance?: number };
  invoices: Array<{ id: string; invoiceNumber?: string; date: string; status: string; total: number; paid: number; balance: number; service: string }>;
};
type PrintablePayload = { invoice?: InvoiceRow; receipt?: unknown; generatedAt: string };

type DraftItem = {
  localId: string;
  serviceId?: string;
  productId?: string;
  serviceChargeId?: string;
  appointmentTypeId?: string | null;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
};

type ServiceChargeRow = {
  id: string;
  serviceId: string;
  serviceName: string;
  name: string;
  code: string;
  amount: number;
  type: 'flat' | 'percent';
  active: boolean;
};

type AppointmentTypeChargeSource = {
  id: string;
  name: string;
  serviceDocumentation?: Record<string, unknown>;
};

type BillingModuleProps = {
  patientId: string;
  showHeader?: boolean;
  initialView?: 'list' | 'create' | 'detail';
  mode?: 'full' | 'createOnly';
  onCancelCreate?: () => void;
  onInvoiceCreated?: (invoice: InvoiceRow) => void;
  initialSelectedInvoiceId?: string | null;
};

const currencyOptions = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR', 'AED', 'BRL', 'CHF', 'CNY', 'MXN', 'NZD', 'SEK', 'SGD', 'ZAR'];

type PhraseTranslator = (phrase: string) => string;

const billingStatusLabels: Record<BillingStatus, string> = {
  UNPAID: 'Unpaid',
  PENDING: 'Pending',
  PAID: 'Paid',
  REFUNDED: 'Refunded',
};

const paymentMethodLabels: Record<string, string> = {
  CARD: 'Card',
  CASH: 'Cash',
  CHECK: 'Check',
  WIRE: 'Wire',
  INSURANCE: 'Insurance',
};

const billingItOverrides: Record<string, string> = {
  Billing: 'Fatturazione',
  'Patient invoices and payments': 'Area di fatturazione paziente',
  Refresh: 'Aggiorna',
  Invoices: 'Fatture',
  'Total Billed': 'Totale fatturato',
  Outstanding: 'Residuo',
  Unpaid: 'Non pagata',
  Payments: 'Pagamenti',
  'New Invoice': 'Nuova fattura',
  'Loading...': 'Caricamento...',
  'Search invoice # or line item...': 'Cerca fattura n. o voce...',
  'All statuses': 'Tutti gli stati',
  Pending: 'In attesa',
  Paid: 'Pagata',
  Refunded: 'Rimborsata',
  Invoice: 'Fattura',
  Date: 'Data',
  Status: 'Stato',
  Total: 'Totale',
  Balance: 'Saldo',
  'No invoices found.': 'Nessuna fattura trovata.',
  Back: 'Indietro',
  'Search billables...': 'Cerca elementi fatturabili...',
  'No catalog items found.': 'Nessun elemento di catalogo trovato.',
  Provider: 'Medico',
  'Select provider...': 'Seleziona medico...',
  Appointment: 'Appuntamento',
  'Select appointment...': 'Seleziona appuntamento...',
  'Due Date': 'Data di scadenza',
  'Tax %': 'Imposta %',
  Discount: 'Sconto',
  Notes: 'Note',
  Code: 'Codice',
  Description: 'Descrizione',
  Qty: 'Qta',
  Price: 'Prezzo',
  Subtotal: 'Subtotale',
  Tax: 'Imposta',
  'No line items yet.': 'Nessuna voce ancora presente.',
  'Saving...': 'Salvataggio...',
  'Create Invoice': 'Crea fattura',
  'Invoice Details': 'Dettagli fattura',
  'Due date': 'Data di scadenza',
  'Save Details': 'Salva dettagli',
  'Post Payment': 'Registra pagamento',
  Amount: 'Importo',
  Unit: 'Unita',
  Reference: 'Riferimento',
  Card: 'Carta',
  Cash: 'Contanti',
  Check: 'Assegno',
  Wire: 'Bonifico',
  Insurance: 'Assicurazione',
  'Processing...': 'Elaborazione...',
  'Apply Payment': 'Applica pagamento',
  Method: 'Metodo',
  'No payments recorded.': 'Nessun pagamento registrato.',
  'No payments posted yet.': 'Nessun pagamento registrato finora.',
  'Failed to load billing data.': 'Impossibile caricare i dati di fatturazione.',
  'Add at least one line item.': 'Aggiungi almeno una voce.',
  'Failed to save invoice.': 'Impossibile salvare la fattura.',
  'Enter a valid payment amount.': 'Inserisci un importo di pagamento valido.',
  'Payment posted.': 'Pagamento registrato.',
  'Failed to post payment.': 'Impossibile registrare il pagamento.',
  'Invoice details updated.': 'Dettagli fattura aggiornati.',
  'Failed to update invoice details.': 'Impossibile aggiornare i dettagli della fattura.',
};

const billingEsOverrides: Record<string, string> = {
  Billing: 'Facturacion',
  'Patient invoices and payments': 'Espacio de facturacion del paciente',
  Refresh: 'Actualizar',
  Invoices: 'Facturas',
  'Total Billed': 'Total facturado',
  Outstanding: 'Pendiente',
  Unpaid: 'No pagada',
  Payments: 'Pagos',
  'New Invoice': 'Nueva factura',
  'Loading...': 'Cargando...',
  'Search invoice # or line item...': 'Buscar factura n. o linea...',
  'All statuses': 'Todos los estados',
  Pending: 'Pendiente',
  Paid: 'Pagada',
  Refunded: 'Reembolsada',
  Invoice: 'Factura',
  Date: 'Fecha',
  Status: 'Estado',
  Total: 'Total',
  Balance: 'Saldo',
  'No invoices found.': 'No se encontraron facturas.',
  Back: 'Volver',
  'Search billables...': 'Buscar conceptos facturables...',
  'No catalog items found.': 'No se encontraron elementos del catalogo.',
  Provider: 'Profesional',
  'Select provider...': 'Seleccionar profesional...',
  Appointment: 'Cita',
  'Select appointment...': 'Seleccionar cita...',
  'Due Date': 'Fecha de vencimiento',
  'Tax %': 'Impuesto %',
  Discount: 'Descuento',
  Notes: 'Notas',
  Code: 'Codigo',
  Description: 'Descripcion',
  Qty: 'Cant.',
  Price: 'Precio',
  Subtotal: 'Subtotal',
  Tax: 'Impuesto',
  'No line items yet.': 'Aun no hay lineas.',
  'Saving...': 'Guardando...',
  'Create Invoice': 'Crear factura',
  'Invoice Details': 'Detalles de la factura',
  'Due date': 'Fecha de vencimiento',
  'Save Details': 'Guardar detalles',
  'Post Payment': 'Registrar pago',
  Amount: 'Monto',
  Unit: 'Unidad',
  Reference: 'Referencia',
  Card: 'Tarjeta',
  Cash: 'Efectivo',
  Check: 'Cheque',
  Wire: 'Transferencia',
  Insurance: 'Seguro',
  'Processing...': 'Procesando...',
  'Apply Payment': 'Aplicar pago',
  Method: 'Metodo',
  'No payments recorded.': 'No hay pagos registrados.',
  'No payments posted yet.': 'Aun no hay pagos registrados.',
  'Failed to load billing data.': 'No se pudieron cargar los datos de facturacion.',
  'Add at least one line item.': 'Agrega al menos una linea.',
  'Failed to save invoice.': 'No se pudo guardar la factura.',
  'Enter a valid payment amount.': 'Ingresa un monto de pago valido.',
  'Payment posted.': 'Pago registrado.',
  'Failed to post payment.': 'No se pudo registrar el pago.',
  'Invoice details updated.': 'Detalles de la factura actualizados.',
  'Failed to update invoice details.': 'No se pudieron actualizar los detalles de la factura.',
};

function createBillingTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;

    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }

    if (language === 'es' || language === 'it') {
      const direct = language === 'it' ? billingItOverrides[normalized] : billingEsOverrides[normalized];
      if (direct) return phrase.replace(normalized, direct);
    }

    return phrase;
  };
}

function formatAppointmentLabel(appointment: AppointmentRow) {
  const dateLabel = new Date(appointment.startTime).toLocaleString();
  return appointment.appointmentType?.name ? `${dateLabel} · ${appointment.appointmentType.name}` : dateLabel;
}


function normalizeServiceCharges(rows: AppointmentTypeChargeSource[]): ServiceChargeRow[] {
  return rows.flatMap((service) => {
    const docs = service.serviceDocumentation && typeof service.serviceDocumentation === 'object' ? service.serviceDocumentation as Record<string, any> : {};
    const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
    const charges = Array.isArray(meta.serviceCharges) ? meta.serviceCharges : [];
    return charges
      .map((charge: Record<string, unknown>) => ({
        id: String(charge.id || `${service.id}-${String(charge.name || 'charge')}`),
        serviceId: service.id,
        serviceName: service.name,
        name: String(charge.name || 'Service charge'),
        code: String(charge.code || 'SVC-CHARGE').toUpperCase(),
        amount: Number(charge.amount || 0),
        type: charge.type === 'percent' ? 'percent' as const : 'flat' as const,
        active: Boolean(charge.active ?? true),
      }))
      .filter((charge: ServiceChargeRow) => charge.active && charge.amount > 0);
  });
}

export function BillingModule({
  patientId,
  showHeader = true,
  initialView = 'list',
  mode: _mode,
  onCancelCreate,
  onInvoiceCreated,
  initialSelectedInvoiceId = null,
}: BillingModuleProps) {
  const { fetchJson } = useApiClient();
  const { tr, language } = useI18n();
  const t = useMemo(() => createBillingTranslator(language, tr), [language, tr]);
  const [tab, setTab] = useState<'Invoices' | 'Payments'>('Invoices');
  const [view, setView] = useState<'list' | 'create' | 'detail'>(initialSelectedInvoiceId ? 'detail' : initialView);
  const [currency, setCurrency] = useState('USD');
  const { locale, currency: orgCurrency } = useOrgFormatting();
  // Locale-aware money formatter (shadows the old module helper): formats in the
  // org's locale (e.g. es-MX) so amounts render natively, not always en-US.
  const money = (value: number, cur: string = currency) => formatCurrency(value, { currency: cur, locale });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [catalog, setCatalog] = useState<CatalogResponse>({ services: [], products: [] });
  const [serviceCharges, setServiceCharges] = useState<ServiceChargeRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [summary, setSummary] = useState<SummaryResponse>({
    invoiceCount: 0,
    totalBilled: 0,
    outstandingBalance: 0,
    unpaidCount: 0,
    pendingCount: 0,
    paidCount: 0,
  });
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [searchCatalog, setSearchCatalog] = useState('');
  const [searchInvoices, setSearchInvoices] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | BillingStatus>('ALL');
  const [draftItems, setDraftItems] = useState<DraftItem[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [taxPercent, setTaxPercent] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CARD');
  const [paymentReference, setPaymentReference] = useState('');
  const [detailStatus, setDetailStatus] = useState<BillingStatus>('UNPAID');
  const [detailDueDate, setDetailDueDate] = useState('');
  const [detailNotes, setDetailNotes] = useState('');
  const [updatingDetail, setUpdatingDetail] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [auditRows, setAuditRows] = useState<InvoiceAuditRow[]>([]);
  const [statement, setStatement] = useState<FinancialStatement | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [creditNoteAmount, setCreditNoteAmount] = useState('');
  const [accountCreditAmount, setAccountCreditAmount] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [transferPatientId, setTransferPatientId] = useState('');
  const [balanceTransferPatientId, setBalanceTransferPatientId] = useState('');
  const [balanceTransferAmount, setBalanceTransferAmount] = useState('');
  const [invoicePreview, setInvoicePreview] = useState<PrintablePayload | null>(null);

  const selectedInvoice = useMemo(
    () => (selectedInvoiceId ? invoices.find((invoice) => invoice.id === selectedInvoiceId) || null : null),
    [invoices, selectedInvoiceId],
  );

  useEffect(() => {
    const cached = localStorage.getItem('billing.currency');
    if (cached && currencyOptions.includes(cached)) {
      setCurrency(cached);
    } else if (orgCurrency && currencyOptions.includes(orgCurrency)) {
      // No prior choice: default to the org's configured currency (e.g. MXN).
      setCurrency(orgCurrency);
    }
  }, [orgCurrency]);

  useEffect(() => {
    localStorage.setItem('billing.currency', currency);
  }, [currency]);

  useEffect(() => {
    if (!initialSelectedInvoiceId) return;
    if (!invoices.some((invoice) => invoice.id === initialSelectedInvoiceId)) return;
    setSelectedInvoiceId(initialSelectedInvoiceId);
    setTab('Invoices');
    setView('detail');
  }, [initialSelectedInvoiceId, invoices]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const [catalogData, invoiceData, providerData, appointmentData, summaryData] = await Promise.all([
        fetchJson<CatalogResponse>('/billing/catalog'),
        fetchJson<InvoiceRow[]>(`/billing?patientId=${patientId}`),
        fetchJson<ProviderRow[]>('/billing/providers'),
        fetchJson<AppointmentRow[]>(`/appointments?patientId=${patientId}`),
        fetchJson<SummaryResponse>(`/billing/summary?patientId=${patientId}`),
      ]);
      setCatalog(catalogData);
      try {
        const chargeSources = await fetchJson<AppointmentTypeChargeSource[]>('/appointment-types');
        setServiceCharges(normalizeServiceCharges(chargeSources || []));
      } catch {
        setServiceCharges([]);
      }
      setInvoices(invoiceData);
      setProviders(providerData);
      setAppointments(appointmentData);
      setSummary(summaryData);
      setSelectedInvoiceId((current) =>
        current && !invoiceData.some((invoice) => invoice.id === current) ? null : current,
      );
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to load billing data.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, patientId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const catalogRows = useMemo(() => {
    const q = searchCatalog.toLowerCase();
    return [...catalog.services.map((s) => ({ ...s, kind: 'service' as const })), ...catalog.products.map((p) => ({ ...p, kind: 'product' as const }))]
      .filter((row) => {
        if (!q) return true;
        const searchable = [
          row.code,
          row.name,
          row.description,
          row.kind === 'service' ? row.serviceCode : '',
          row.kind === 'service' ? row.iscCode : '',
          row.kind === 'service' ? row.ccsdCode : '',
        ];
        return searchable.some((value) => String(value || '').toLowerCase().includes(q));
      });
  }, [catalog, searchCatalog]);

  const availableServiceCharges = useMemo(() => {
    const selectedServiceIds = new Set(draftItems.map((item) => item.serviceId).filter(Boolean));
    const selectedServiceNames = new Set(
      draftItems
        .filter((item) => item.serviceId)
        .map((item) => item.description.toLowerCase()),
    );
    const usedChargeIds = new Set(draftItems.map((item) => item.serviceChargeId).filter(Boolean));
    return serviceCharges.filter((charge) => {
      if (usedChargeIds.has(charge.id)) return false;
      if (!selectedServiceIds.size) return true;
      return selectedServiceIds.has(charge.serviceId) || selectedServiceNames.has(charge.serviceName.toLowerCase());
    });
  }, [draftItems, serviceCharges]);

  const filteredInvoices = useMemo(() => {
    const q = searchInvoices.toLowerCase();
    return invoices.filter((inv) => {
      if (statusFilter !== 'ALL' && inv.status !== statusFilter) return false;
      return !q || (inv.invoiceNumber || '').toLowerCase().includes(q) || inv.items.some((item) => item.code.toLowerCase().includes(q) || item.description.toLowerCase().includes(q));
    });
  }, [invoices, searchInvoices, statusFilter]);

  const subtotal = draftItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxAmount = subtotal * (taxPercent / 100);
  const total = Math.max(0, subtotal + taxAmount - discount);

  const paymentsFeed = useMemo(
    () =>
      invoices
        .flatMap((inv) => inv.payments.map((pay) => ({ ...pay, invoiceNumber: inv.invoiceNumber || inv.id })))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [invoices],
  );

  useEffect(() => {
    if (!selectedInvoice) return;
    setDetailStatus(selectedInvoice.status);
    setDetailDueDate(selectedInvoice.dueDate ? selectedInvoice.dueDate.slice(0, 10) : '');
    setDetailNotes(selectedInvoice.notes || '');
  }, [selectedInvoice?.id, selectedInvoice?.status, selectedInvoice?.dueDate, selectedInvoice?.notes]);

  function addCatalogLine(row: (typeof catalogRows)[number]) {
    setDraftItems((prev) => {
      const existing = prev.find((item) => (row.kind === 'service' ? item.serviceId === row.id : item.productId === row.id));
      if (existing) {
        return prev.map((item) => (item.localId === existing.localId ? { ...item, quantity: item.quantity + 1 } : item));
      }
      return [
        ...prev,
        {
          localId: Math.random().toString(36).slice(2, 11),
          serviceId: row.kind === 'service' ? row.id : undefined,
          productId: row.kind === 'product' ? row.id : undefined,
          appointmentTypeId: row.kind === 'service' ? row.appointmentTypeId || null : null,
          code: row.code,
          description: row.name,
          quantity: 1,
          unitPrice: Number(row.price || 0),
        },
      ];
    });
  }

  function addServiceChargeLine(charge: ServiceChargeRow) {
    const base = subtotal > 0 ? subtotal : draftItems.reduce((sum, item) => item.serviceChargeId ? sum : sum + item.quantity * item.unitPrice, 0);
    const unitPrice = charge.type === 'percent' ? base * (charge.amount / 100) : charge.amount;
    setDraftItems((prev) => [
      ...prev,
      {
        localId: Math.random().toString(36).slice(2, 11),
        serviceChargeId: charge.id,
        code: charge.code,
        description: `${charge.name} (${charge.serviceName})`,
        quantity: 1,
        unitPrice: Number(unitPrice.toFixed(2)),
      },
    ]);
  }

  async function saveInvoice() {
    if (!draftItems.length) {
      setFailure(t('Add at least one line item.'));
      return;
    }
    setSaving(true);
    setFailure(null);
    setBanner(null);
    try {
      const created = await fetchJson<InvoiceRow>('/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId,
          providerId: selectedProviderId || undefined,
          appointmentId: selectedAppointmentId || undefined,
          dueDate: dueDate || undefined,
          notes: notes || undefined,
          status: 'UNPAID',
          subtotal,
          tax: taxAmount,
          discount,
          total,
          items: draftItems.map((item) => ({
            serviceId: item.serviceId,
            productId: item.productId,
            code: item.code,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.quantity * item.unitPrice,
          })),
        }),
      });

      setDraftItems([]);
      setSelectedProviderId('');
      setSelectedAppointmentId('');
      setDueDate('');
      setNotes('');
      setTaxPercent(0);
      setDiscount(0);
      setBanner(`${t('Invoice')} ${created.invoiceNumber || ''} ${language === 'it' ? 'creata.' : 'created.'}`);
      onInvoiceCreated?.(created);
      await loadAll();
      setSelectedInvoiceId(created.id);
      setView('detail');
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to save invoice.'));
    } finally {
      setSaving(false);
    }
  }

  async function submitPayment() {
    if (!selectedInvoice) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setFailure(t('Enter a valid payment amount.'));
      return;
    }
    setPaying(true);
    setFailure(null);
    setBanner(null);
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          method: paymentMethod,
          reference: paymentReference || undefined,
        }),
      });
      setSelectedInvoiceId(updated.id);
      setPaymentAmount('');
      setPaymentMethod('CARD');
      setPaymentReference('');
      setBanner(t('Payment posted.'));
      await loadAll();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to post payment.'));
    } finally {
      setPaying(false);
    }
  }

  async function updateInvoiceDetails() {
    if (!selectedInvoice) return;
    setUpdatingDetail(true);
    setFailure(null);
    setBanner(null);
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: detailStatus,
          dueDate: detailDueDate || null,
          notes: detailNotes || undefined,
        }),
      });
      setSelectedInvoiceId(updated.id);
      setBanner(t('Invoice details updated.'));
      await loadAll();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to update invoice details.'));
    } finally {
      setUpdatingDetail(false);
    }
  }

  async function loadInvoiceAudit() {
    if (!selectedInvoice) return;
    try {
      setAuditRows(await fetchJson<InvoiceAuditRow[]>(`/billing/${selectedInvoice.id}/audit`));
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to load invoice audit logs.'));
    }
  }

  async function loadStatement() {
    try {
      setStatement(await fetchJson<FinancialStatement>(`/billing/statement/${patientId}`));
      setBanner(t('Financial statement loaded.'));
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to load financial statement.'));
    }
  }

  async function refundInvoice(processor: 'MANUAL' | 'STRIPE', full = false) {
    if (!selectedInvoice) return;
    const amount = full ? selectedInvoice.total - selectedInvoice.balance : Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setFailure(t('Enter a valid refund amount.'));
      return;
    }
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, processor, reason: processor === 'STRIPE' ? 'Stripe refund from client card' : 'Invoice refund from client card' }),
      });
      setSelectedInvoiceId(updated.id);
      setRefundAmount('');
      setBanner(full ? t('Invoice refunded.') : t('Partial refund processed.'));
      await loadAll();
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to process refund.'));
    }
  }

  async function voidCreditNote() {
    if (!selectedInvoice) return;
    try {
      await fetchJson(`/billing/${selectedInvoice.id}/credit-note/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Voided from client card' }),
      });
      setBanner(t('Credit note voided.'));
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to void credit note.'));
    }
  }

  async function createInvoiceFromAppointment() {
    const appointmentId = selectedAppointmentId || appointments[0]?.id;
    if (!appointmentId) {
      setFailure(t('Select appointment...'));
      return;
    }
    try {
      const created = await fetchJson<InvoiceRow>('/billing/from-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId, notes: 'Raised from client card appointment invoicing' }),
      });
      setBanner(`${t('Invoice')} ${created.invoiceNumber || ''} ${language === 'it' ? 'creata.' : 'created.'}`);
      await loadAll();
      setSelectedInvoiceId(created.id);
      setView('detail');
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to save invoice.'));
    }
  }

  async function voidInvoice() {
    if (!selectedInvoice) return;
    if (!window.confirm(t('Void this invoice?'))) return;
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Voided from client card' }),
      });
      setSelectedInvoiceId(updated.id);
      setBanner(t('Invoice voided.'));
      await loadAll();
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to void invoice.'));
    }
  }

  async function issueCreditNote() {
    if (!selectedInvoice) return;
    const amount = Number(creditNoteAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setFailure(t('Enter a valid credit note amount.'));
      return;
    }
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/credit-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, reason: 'Credit note from client card' }),
      });
      setSelectedInvoiceId(updated.id);
      setCreditNoteAmount('');
      setBanner(t('Credit note issued.'));
      await loadAll();
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to issue credit note.'));
    }
  }

  async function addAccountCredit() {
    const amount = Number(accountCreditAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setFailure(t('Enter a valid account credit amount.'));
      return;
    }
    try {
      await fetchJson(`/billing/patient/${patientId}/account-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, method: 'CARD', note: 'Client account balance top-up' }),
      });
      setAccountCreditAmount('');
      setBanner(t('Client account balance updated.'));
      await loadStatement();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to add account credit.'));
    }
  }

  async function transferAccountBalance() {
    const amount = Number(balanceTransferAmount);
    if (!balanceTransferPatientId.trim() || !Number.isFinite(amount) || amount <= 0) {
      setFailure(t('Enter a target client and amount.'));
      return;
    }
    try {
      await fetchJson(`/billing/patient/${patientId}/balance-transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPatientId: balanceTransferPatientId.trim(), amount, reason: 'Balance transfer from client card' }),
      });
      setBalanceTransferPatientId('');
      setBalanceTransferAmount('');
      setBanner(t('Balance transferred.'));
      await loadStatement();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to transfer balance.'));
    }
  }

  async function addCardDetails() {
    if (!/^\d{4}$/.test(cardLast4)) {
      setFailure(t('Enter the last 4 card digits.'));
      return;
    }
    try {
      await fetchJson(`/billing/patient/${patientId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: 'Card', last4: cardLast4, expiryMonth: 12, expiryYear: new Date().getFullYear() + 3 }),
      });
      setCardLast4('');
      setBanner(t('Card details added.'));
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to add card details.'));
    }
  }

  async function shareInvoice() {
    if (!selectedInvoice) return;
    try {
      await fetchJson(`/billing/${selectedInvoice.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: 'Invoice shared from client card' }),
      });
      setBanner(t('Invoice shared.'));
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to share invoice.'));
    }
  }

  async function previewInvoice() {
    if (!selectedInvoice) return;
    try {
      setInvoicePreview(await fetchJson<PrintablePayload>(`/billing/${selectedInvoice.id}/preview`));
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to preview invoice.'));
    }
  }

  async function printInvoice() {
    if (!selectedInvoice) return;
    try {
      const printable = await fetchJson(`/billing/${selectedInvoice.id}/print`, { method: 'POST' });
      const blob = new Blob([JSON.stringify(printable, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.download = `${selectedInvoice.invoiceNumber || selectedInvoice.id}-print.json`;
      linkEl.click();
      URL.revokeObjectURL(url);
      setBanner(t('Invoice print file generated.'));
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to print invoice.'));
    }
  }

  async function transferInvoice() {
    if (!selectedInvoice) return;
    if (!transferPatientId.trim()) {
      setFailure(t('Enter a target client.'));
      return;
    }
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPatientId: transferPatientId.trim(), reason: 'Transferred from client card' }),
      });
      setTransferPatientId('');
      setSelectedInvoiceId(updated.id);
      setBanner(t('Invoice transferred.'));
      await loadAll();
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to transfer invoice.'));
    }
  }

  async function printReceipt(payment: PaymentRow) {
    if (!selectedInvoice) return;
    try {
      const printable = await fetchJson<PrintablePayload>(`/billing/${selectedInvoice.id}/payments/${payment.id}/receipt`, { method: 'POST' });
      const blob = new Blob([JSON.stringify(printable, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const linkEl = document.createElement('a');
      linkEl.href = url;
      linkEl.download = `${selectedInvoice.invoiceNumber || selectedInvoice.id}-${payment.id}-receipt.json`;
      linkEl.click();
      URL.revokeObjectURL(url);
      setBanner(t('Receipt print file generated.'));
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to print receipt.'));
    }
  }

  async function voidPayment(paymentId: string) {
    if (!selectedInvoice) return;
    if (!window.confirm(t('Void this payment?'))) return;
    try {
      const updated = await fetchJson<InvoiceRow>(`/billing/${selectedInvoice.id}/payments/${paymentId}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Voided from client card' }),
      });
      setSelectedInvoiceId(updated.id);
      setBanner(t('Payment voided.'));
      await loadAll();
      await loadInvoiceAudit();
    } catch (err: any) {
      setFailure(t(err.message || 'Failed to void payment.'));
    }
  }

  function handleBackFromCreate() {
    if (onCancelCreate) {
      onCancelCreate();
      return;
    }
    setView('list');
  }

  return (
    <div className="space-y-4">
      {showHeader ? (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">{t('Billing')}</p>
              <h3 className="text-base font-semibold text-slate-900">{t('Patient invoices and payments')}</h3>
            </div>
            <div className="flex items-center gap-2">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm">
                {currencyOptions.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              <button onClick={loadAll} className="px-3 py-1.5 rounded border border-slate-300 bg-white text-sm hover:bg-slate-50">
                {t('Refresh')}
              </button>
              <button onClick={loadStatement} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-sm hover:bg-slate-50">
                <ReceiptText size={14} /> {t('Statement')}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 px-4 py-4 md:grid-cols-4">
            <MetricCard label={t('Invoices')} value={String(summary.invoiceCount)} />
            <MetricCard label={t('Total Billed')} value={money(summary.totalBilled, currency)} />
            <MetricCard label={t('Outstanding')} value={money(summary.outstandingBalance, currency)} />
            <MetricCard label={t('Unpaid')} value={String(summary.unpaidCount)} />
          </div>
        </div>
      ) : null}

      {banner && <Banner kind="success" text={banner} />}
      {failure && <Banner kind="error" text={failure} />}
      {statement ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-900">{t('Financial Statement')}</p>
              <p className="text-xs text-slate-500">{new Date(statement.generatedAt).toLocaleString()}</p>
            </div>
            <button className="text-xs text-slate-500 hover:text-ink" onClick={() => setStatement(null)}>{t('Close')}</button>
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-3">
            <MetricCard label={t('Total Billed')} value={money(statement.totals.billed, currency)} />
            <MetricCard label={t('Payments')} value={money(statement.totals.paid, currency)} />
            <MetricCard label={t('Balance')} value={money(statement.totals.balance, currency)} />
            <MetricCard label={t('Client Account Balance')} value={money(statement.totals.accountBalance || 0, currency)} />
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-800">{t('Client Account Balance')}</div>
            <div className="flex gap-2">
              <input type="number" min={0} step="0.01" value={accountCreditAmount} onChange={(e) => setAccountCreditAmount(e.target.value)} placeholder={t('Amount')} className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-2 text-sm" />
              <button onClick={addAccountCredit} className="rounded bg-ink px-3 py-2 text-sm font-medium text-white">{t('Add money')}</button>
            </div>
            <div className="grid gap-2 md:grid-cols-[1fr_120px_auto]">
              <input value={balanceTransferPatientId} onChange={(e) => setBalanceTransferPatientId(e.target.value)} placeholder={t('Target client ID')} className="min-w-0 rounded border border-slate-300 px-2 py-2 text-sm" />
              <input type="number" min={0} step="0.01" value={balanceTransferAmount} onChange={(e) => setBalanceTransferAmount(e.target.value)} placeholder={t('Amount')} className="rounded border border-slate-300 px-2 py-2 text-sm" />
              <button onClick={transferAccountBalance} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">{t('Transfer balance')}</button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-800">{t('Card Details')}</div>
            <div className="flex gap-2">
              <input value={cardLast4} onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder={t('Last 4 digits')} className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-2 text-sm" />
              <button onClick={addCardDetails} className="rounded bg-ink px-3 py-2 text-sm font-medium text-white">{t('Add card')}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 p-3 flex items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => setTab('Invoices')} className={`px-3 py-1.5 rounded text-sm ${tab === 'Invoices' ? 'bg-ink text-white' : 'bg-slate-100 text-slate-700'}`}>{t('Invoices')}</button>
            <button onClick={() => { setTab('Payments'); setView('list'); }} className={`px-3 py-1.5 rounded text-sm ${tab === 'Payments' ? 'bg-ink text-white' : 'bg-slate-100 text-slate-700'}`}>{t('Payments')}</button>
          </div>
          {tab === 'Invoices' && view === 'list' && (
            <div className="flex flex-wrap gap-2">
              <button onClick={createInvoiceFromAppointment} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <FilePlus2 size={14} /> {t('Invoice appointment')}
              </button>
              <button onClick={() => setView('create')} className="inline-flex items-center gap-1 rounded-xl bg-lime-400 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-lime-300">
                <FilePlus2 size={14} /> {t('New Invoice')}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t('Loading...')}
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {tab === 'Invoices' && view === 'list' && (
              <>
                <div className="flex flex-wrap gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <input value={searchInvoices} onChange={(e) => setSearchInvoices(e.target.value)} className="pl-8 pr-3 py-2 border border-slate-300 rounded text-sm min-w-[260px]" placeholder={t('Search invoice # or line item...')} />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'ALL' | BillingStatus)} className="border border-slate-300 rounded px-3 py-2 text-sm">
                    <option value="ALL">{t('All statuses')}</option>
                    <option value="UNPAID">{t('Unpaid')}</option>
                    <option value="PENDING">{t('Pending')}</option>
                    <option value="PAID">{t('Paid')}</option>
                    <option value="REFUNDED">{t('Refunded')}</option>
                  </select>
                </div>

                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
                    <tr>
                      <th className="p-3">{t('Invoice')}</th>
                      <th className="p-3">{t('Date')}</th>
                      <th className="p-3">{t('Status')}</th>
                      <th className="p-3 text-right">{t('Total')}</th>
                      <th className="p-3 text-right">{t('Balance')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredInvoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => { setSelectedInvoiceId(inv.id); setView('detail'); }}>
                        <td className="p-3 font-medium text-slate-800">{inv.invoiceNumber || inv.id}</td>
                        <td className="p-3">{new Date(inv.date).toLocaleDateString()}</td>
                        <td className="p-3"><StatusPill status={inv.status} label={t(billingStatusLabels[inv.status])} /></td>
                        <td className="p-3 text-right">{money(inv.total, currency)}</td>
                        <td className="p-3 text-right font-medium">{money(inv.balance, currency)}</td>
                      </tr>
                    ))}
                    {!filteredInvoices.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">{t('No invoices found.')}</td></tr>}
                  </tbody>
                </table>
              </>
            )}

            {tab === 'Invoices' && view === 'create' && (
              <>
                <button onClick={handleBackFromCreate} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-sm hover:bg-slate-50">
                  <ArrowLeft size={14} /> {t('Back')}
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-slate-200">
                    <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                      <Search size={14} className="text-slate-400" />
                      <input value={searchCatalog} onChange={(e) => setSearchCatalog(e.target.value)} className="w-full text-sm outline-none" placeholder={t('Search billables...')} />
                    </div>
                    <div className="max-h-[360px] overflow-auto">
                      {catalogRows.map((row) => (
                        <button key={`${row.kind}-${row.id}`} className="w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50" onClick={() => addCatalogLine(row)}>
                          <div className="flex justify-between gap-2">
                            <div>
                              <div className="font-medium text-slate-800">{row.code} • {row.name}</div>
                              <div className="text-xs text-slate-500">{row.description || ''}</div>
                            </div>
                            <div className="text-sm font-medium text-slate-700">{money(row.price, currency)}</div>
                          </div>
                        </button>
                      ))}
                      {!catalogRows.length && <div className="p-4 text-sm text-slate-500">{t('No catalog items found.')}</div>}
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label={t('Provider')}>
                        <select value={selectedProviderId} onChange={(e) => setSelectedProviderId(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-2 text-sm">
                          <option value="">{t('Select provider...')}</option>
                          {providers.map((p) => <option key={p.id} value={p.id}>{p.lastName}, {p.firstName}</option>)}
                        </select>
                      </Field>
                      <Field label={t('Appointment')}>
                        <select value={selectedAppointmentId} onChange={(e) => setSelectedAppointmentId(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-2 text-sm">
                          <option value="">{t('Select appointment...')}</option>
                          {appointments.map((a) => <option key={a.id} value={a.id}>{formatAppointmentLabel(a)}</option>)}
                        </select>
                      </Field>
                      <Field label={t('Due Date')}><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-2 text-sm" /></Field>
                      <Field label={t('Tax %')}><input type="number" min={0} step="0.01" value={taxPercent} onChange={(e) => setTaxPercent(Number(e.target.value || 0))} className="w-full border border-slate-300 rounded px-2 py-2 text-sm" /></Field>
                      <Field label={t('Discount')}><input type="number" min={0} step="0.01" value={discount} onChange={(e) => setDiscount(Number(e.target.value || 0))} className="w-full border border-slate-300 rounded px-2 py-2 text-sm" /></Field>
                    </div>
                    <Field label={t('Notes')}><textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-2 text-sm h-20 resize-none" /></Field>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t('Service Charges')}</p>
                      <p className="text-xs text-slate-500">{t('Apply service charges during checkout. Applied charges can be edited or removed below.')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const appointmentTypeId = draftItems.find((item) => item.appointmentTypeId)?.appointmentTypeId || '';
                          const params = new URLSearchParams({
                            patientId,
                            returnTo: window.location.pathname + window.location.search,
                          });
                          if (appointmentTypeId) params.set('appointmentTypeId', appointmentTypeId);
                          window.location.href = `/packages?${params.toString()}`;
                        }}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        {t('Sell package')}
                      </button>
                      <span className="text-xs text-slate-500">{availableServiceCharges.length} {t('available')}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {availableServiceCharges.map((charge) => (
                      <button
                        key={charge.id}
                        type="button"
                        onClick={() => addServiceChargeLine(charge)}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm hover:bg-slate-100"
                      >
                        <span className="font-medium text-slate-800">{charge.name}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          {charge.type === 'percent' ? `${charge.amount}%` : money(charge.amount, currency)} · {charge.serviceName}
                        </span>
                      </button>
                    ))}
                    {!availableServiceCharges.length ? <span className="text-sm text-slate-500">{t('No service charges available for the selected services.')}</span> : null}
                  </div>
                </div>

                <table className="w-full text-sm text-left rounded-lg border border-slate-200 overflow-hidden">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="p-3">{t('Code')}</th>
                      <th className="p-3">{t('Description')}</th>
                      <th className="p-3 text-right">{t('Qty')}</th>
                      <th className="p-3 text-right">{t('Price')}</th>
                      <th className="p-3 text-right">{t('Total')}</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {draftItems.map((item) => (
                      <tr key={item.localId}>
                        <td className="p-3 font-medium">{item.code}</td>
                        <td className="p-3">
                          {item.description}
                          {item.serviceChargeId ? <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{t('Service charge')}</span> : null}
                        </td>
                        <td className="p-3 text-right"><input type="number" min={1} value={item.quantity} onChange={(e) => setDraftItems((prev) => prev.map((p) => p.localId === item.localId ? { ...p, quantity: Number(e.target.value || 1) } : p))} className="w-16 border border-slate-300 rounded px-2 py-1 text-right" /></td>
                        <td className="p-3 text-right"><input type="number" min={0} step="0.01" value={item.unitPrice} onChange={(e) => setDraftItems((prev) => prev.map((p) => p.localId === item.localId ? { ...p, unitPrice: Number(e.target.value || 0) } : p))} className="w-24 border border-slate-300 rounded px-2 py-1 text-right" /></td>
                        <td className="p-3 text-right">{money(item.quantity * item.unitPrice, currency)}</td>
                        <td className="p-3 text-right"><button className="text-rose-600 hover:text-rose-700" onClick={() => setDraftItems((prev) => prev.filter((p) => p.localId !== item.localId))}><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                    {!draftItems.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500">{t('No line items yet.')}</td></tr>}
                  </tbody>
                </table>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-slate-600">{t('Subtotal')} {money(subtotal, currency)} • {t('Tax')} {money(taxAmount, currency)} • {t('Discount')} {money(discount, currency)}</div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-semibold text-slate-900">{t('Total')} {money(total, currency)}</div>
                    <button onClick={saveInvoice} disabled={saving || !draftItems.length} className="inline-flex items-center gap-1 rounded-xl bg-lime-400 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300 disabled:opacity-60">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                      {saving ? t('Saving...') : t('Create Invoice')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {tab === 'Invoices' && view === 'detail' && selectedInvoice && (
              <>
                <div className="flex items-center justify-between">
                  <button onClick={() => { setSelectedInvoiceId(null); setView('list'); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-slate-300 bg-white text-sm hover:bg-slate-50">
                    <ArrowLeft size={14} /> {t('Back')}
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={shareInvoice} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">{t('Share')}</button>
                    <button onClick={previewInvoice} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">{t('Preview')}</button>
                    <button onClick={printInvoice} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">{t('Print')}</button>
                    <button onClick={voidInvoice} className="rounded border border-rose-200 bg-white px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50">{t('Void')}</button>
                    <StatusPill status={selectedInvoice.status} label={t(billingStatusLabels[selectedInvoice.status])} />
                    <span className="text-sm text-slate-600">{selectedInvoice.invoiceNumber || selectedInvoice.id}</span>
                  </div>
                </div>

                {selectedInvoice.appointment?.serviceName ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <div className="font-medium text-slate-900">{selectedInvoice.appointment.serviceName}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {new Date(selectedInvoice.appointment.startTime).toLocaleString()}
                      {selectedInvoice.appointment.performedServiceName && selectedInvoice.appointment.appointmentTypeName && selectedInvoice.appointment.performedServiceName !== selectedInvoice.appointment.appointmentTypeName
                        ? ` · Scheduled as ${selectedInvoice.appointment.appointmentTypeName}`
                        : ''}
                    </div>
                  </div>
                ) : null}

                {invoicePreview ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{t('Invoice Preview')}</p>
                        <p className="text-xs text-slate-500">{new Date(invoicePreview.generatedAt).toLocaleString()}</p>
                      </div>
                      <button className="text-xs text-slate-500 hover:text-ink" onClick={() => setInvoicePreview(null)}>{t('Close')}</button>
                    </div>
                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-xs text-slate-600">
                      {JSON.stringify(invoicePreview.invoice || invoicePreview, null, 2)}
                    </pre>
                  </div>
                ) : null}

                <table className="w-full text-sm text-left rounded-lg border border-slate-200 overflow-hidden">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="p-3">{t('Code')}</th>
                      <th className="p-3">{t('Description')}</th>
                      <th className="p-3 text-right">{t('Qty')}</th>
                      <th className="p-3 text-right">{t('Unit')}</th>
                      <th className="p-3 text-right">{t('Total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedInvoice.items.map((item) => (
                      <tr key={item.id}>
                        <td className="p-3 font-medium">{item.code}</td>
                        <td className="p-3">{item.description}</td>
                        <td className="p-3 text-right">{item.quantity}</td>
                        <td className="p-3 text-right">{money(item.unitPrice, currency)}</td>
                        <td className="p-3 text-right">{money(item.total, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="font-medium text-slate-800">{t('Invoice Details')}</div>
                    <div className="grid grid-cols-1 gap-2">
                      <label className="text-xs text-slate-600">{t('Status')}</label>
                      <select value={detailStatus} onChange={(e) => setDetailStatus(e.target.value as BillingStatus)} className="border border-slate-300 rounded px-2 py-2 text-sm">
                        <option value="UNPAID">{t('Unpaid')}</option>
                        <option value="PENDING">{t('Pending')}</option>
                        <option value="PAID">{t('Paid')}</option>
                        <option value="REFUNDED">{t('Refunded')}</option>
                      </select>
                      <label className="text-xs text-slate-600">{t('Due date')}</label>
                      <input type="date" value={detailDueDate} onChange={(e) => setDetailDueDate(e.target.value)} className="border border-slate-300 rounded px-2 py-2 text-sm" />
                      <label className="text-xs text-slate-600">{t('Notes')}</label>
                      <textarea value={detailNotes} onChange={(e) => setDetailNotes(e.target.value)} className="border border-slate-300 rounded px-2 py-2 text-sm h-20 resize-none" />
                    </div>
                    <button onClick={updateInvoiceDetails} disabled={updatingDetail} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-ink text-white text-sm disabled:opacity-60">
                      {updatingDetail ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                      {updatingDetail ? t('Saving...') : t('Save Details')}
                    </button>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-600">{t('Subtotal')}</span><span>{money(selectedInvoice.subtotal, currency)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{t('Tax')}</span><span>{money(selectedInvoice.tax, currency)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{t('Discount')}</span><span>{money(selectedInvoice.discount, currency)}</span></div>
                    <div className="flex justify-between font-semibold border-t border-slate-200 pt-1"><span>{t('Total')}</span><span>{money(selectedInvoice.total, currency)}</span></div>
                    <div className="flex justify-between font-semibold text-emerald-700"><span>{t('Balance')}</span><span>{money(selectedInvoice.balance, currency)}</span></div>
                  </div>
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="font-medium text-slate-800">{t('Post Payment')}</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input type="number" min={0} step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder={t('Amount')} className="border border-slate-300 rounded px-2 py-2 text-sm" />
                      <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="border border-slate-300 rounded px-2 py-2 text-sm">
                        <option value="CARD">{t('Card')}</option>
                        <option value="CASH">{t('Cash')}</option>
                        <option value="CHECK">{t('Check')}</option>
                        <option value="WIRE">{t('Wire')}</option>
                        <option value="INSURANCE">{t('Insurance')}</option>
                      </select>
                      <input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder={t('Reference')} className="border border-slate-300 rounded px-2 py-2 text-sm" />
                    </div>
                    <button onClick={submitPayment} disabled={paying || selectedInvoice.balance <= 0 || !paymentAmount} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-60">
                      {paying ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                      {paying ? t('Processing...') : t('Apply Payment')}
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-slate-800">{t('Refunds, credit notes, and audit')}</div>
                    <button onClick={loadInvoiceAudit} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50">
                      <History size={14} /> {t('Invoice Audit Logs')}
                    </button>
                  </div>
                  <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                    <input value={transferPatientId} onChange={(e) => setTransferPatientId(e.target.value)} placeholder={t('Transfer invoice to client ID')} className="rounded border border-slate-300 px-2 py-2 text-sm" />
                    <button onClick={transferInvoice} className="rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">{t('Transfer invoice')}</button>
                  </div>
                  <div className="grid gap-2 md:grid-cols-[1fr_auto_auto_auto]">
                    <input type="number" min={0} step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={t('Refund amount')} className="border border-slate-300 rounded px-2 py-2 text-sm" />
                    <button onClick={() => refundInvoice('MANUAL', false)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">
                      <RotateCcw size={14} /> {t('Partial refund')}
                    </button>
                    <button onClick={() => refundInvoice('MANUAL', true)} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">
                      <RotateCcw size={14} /> {t('Full refund')}
                    </button>
                    <button onClick={() => refundInvoice('STRIPE', false)} className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700">
                      <CreditCard size={14} /> {t('Stripe refund')}
                    </button>
                  </div>
                  <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                    <input type="number" min={0} step="0.01" value={creditNoteAmount} onChange={(e) => setCreditNoteAmount(e.target.value)} placeholder={t('Credit note amount')} className="border border-slate-300 rounded px-2 py-2 text-sm" />
                    <button onClick={issueCreditNote} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50">
                      <ReceiptText size={14} /> {t('Issue credit note')}
                    </button>
                  </div>
                  <button onClick={voidCreditNote} className="rounded border border-rose-200 bg-white px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">
                    {t('Void credit note')}
                  </button>
                  {auditRows.length ? (
                    <div className="max-h-48 overflow-auto rounded-lg border border-slate-200">
                      {auditRows.map((row) => (
                        <div key={row.id} className="border-b border-slate-100 px-3 py-2 text-xs last:border-b-0">
                          <span className="font-semibold text-slate-800">{t(row.action)}</span>
                          <span className="text-slate-500"> · {row.entity} · {new Date(row.createdAt).toLocaleString()}{row.userName ? ` · ${row.userName}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <table className="w-full text-sm text-left rounded-lg border border-slate-200 overflow-hidden">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="p-3">{t('Date')}</th>
                      <th className="p-3">{t('Method')}</th>
                      <th className="p-3">{t('Reference')}</th>
                      <th className="p-3 text-right">{t('Amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedInvoice.payments.map((pay) => (
                      <tr key={pay.id}>
                        <td className="p-3">{new Date(pay.createdAt).toLocaleString()}</td>
                        <td className="p-3">{t(paymentMethodLabels[pay.method] || pay.method)}</td>
                        <td className="p-3 text-slate-500">{pay.reference || '-'}</td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <span>{money(pay.amount, currency)}</span>
                            <button className="text-xs text-slate-600 hover:text-ink" onClick={() => printReceipt(pay)}>{t('Receipt')}</button>
                            <button className="text-xs text-rose-600 hover:text-rose-700" onClick={() => voidPayment(pay.id)}>{t('Void')}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!selectedInvoice.payments.length && <tr><td colSpan={4} className="p-6 text-center text-slate-500">{t('No payments recorded.')}</td></tr>}
                  </tbody>
                </table>
              </>
            )}

            {tab === 'Payments' && (
              <table className="w-full text-sm text-left rounded-lg border border-slate-200 overflow-hidden">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="p-3">{t('Date')}</th>
                    <th className="p-3">{t('Invoice')}</th>
                    <th className="p-3">{t('Method')}</th>
                    <th className="p-3">{t('Reference')}</th>
                    <th className="p-3 text-right">{t('Amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paymentsFeed.map((pay) => (
                    <tr key={pay.id}>
                      <td className="p-3">{new Date(pay.createdAt).toLocaleString()}</td>
                      <td className="p-3">{pay.invoiceNumber}</td>
                      <td className="p-3">{t(paymentMethodLabels[pay.method] || pay.method)}</td>
                      <td className="p-3 text-slate-500">{pay.reference || '-'}</td>
                      <td className="p-3 text-right">{money(pay.amount, currency)}</td>
                    </tr>
                  ))}
                  {!paymentsFeed.length && <tr><td colSpan={5} className="p-6 text-center text-slate-500">{t('No payments posted yet.')}</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function StatusPill({ status, label }: { status: BillingStatus; label: string }) {
  const classes =
    status === 'PAID'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'PENDING'
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : status === 'REFUNDED'
      ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
      : 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`px-2 py-1 rounded-full text-xs border ${classes}`}>{label}</span>;
}

function Banner({ kind, text }: { kind: 'success' | 'error'; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
        kind === 'success'
          ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
          : 'text-rose-700 bg-rose-50 border-rose-100'
      }`}
    >
      {kind === 'success' ? <Plus size={14} /> : <AlertTriangle size={14} />}
      {text}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-slate-600">{label}</span>
      {children}
    </label>
  );
}
